import React, { useState, useEffect, useRef, memo, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Controller, useFormContext } from 'react-hook-form';
import SearchComponent from './SearchMC';
import IconMC from './IconMC';
import TagMC from './TagMC';
import TooltipMC from './TooltipMC';
import useClickOutside from '../../hooks/useClickOutside';

/**
 * @typedef {Object} IOptionProps
 * @property {string|number} id - Unique identifier for the option
 * @property {string} label - Display text for the option
 * @property {React.ReactNode} [icon] - Optional icon component or icon class name
 * @property {string} [color] - Optional color in hex format (#AAAAAA)
 */

/**
 * @typedef {Object} MultiSelectDropdownProps
 * @property {string} id - HTML 'id' attribute for the select element
 * @property {string} label - Text label to display above/inside the dropdown
 * @property {Array<IOptionProps>} options - Array of selectable option objects
 * @property {Function} [onSelect] - Callback function called when options are selected/deselected. Signature: (name: string, selectedValues: Array<string|number>) => void
 * @property {Array<string|number>} [defaultValue] - Initial selected values array
 * @property {boolean} [search=false] - Whether to show search input inside dropdown
 * @property {string} [name] - Form field name for react-hook-form integration
 * @property {boolean} [useFloatingLabel=false] - Whether to use floating label style
 * @property {string} [className=''] - Additional CSS classes for dropdown container
 * @property {string} [placeholder] - Placeholder text when no options are selected
 * @property {Function} [filterOption] - Custom filter function. Signature: (searchTerm: string) => Array<IOptionProps>
 * @property {number} [maxSelected] - Maximum number of selectable items
 * @property {Function} [separator] - Optional separator component function between selected tags
 * @property {boolean} [allowCreate=false] - Whether to allow creating new options when search doesn't match
 * @property {Function} [onCreateOption] - Async function for creating new options. Signature: (searchTerm: string) => Promise<IOptionProps>
 * @property {string} [createOptionText='Create'] - Text to show for create option
 * @property {string} [labelTooltip] - Optional tooltip text displayed next to the label
 */

/**
 * Optimized multi-select dropdown component with search, create options, and form integration.
 * Follows Atomic Design principles as a Molecule-level component.
 * 
 * Performance optimizations:
 * - React.memo for preventing unnecessary re-renders
 * - useCallback for stable function references
 * - useMemo for expensive calculations
 * - Optimized filtering and rendering
 * 
 * Features:
 * - Multi-selection with visual tags
 * - Optional search functionality with debounce
 * - Create new options on-the-fly
 * - React Hook Form integration
 * - Floating label support
 * - Maximum selection limit
 * - Custom filtering
 * - Accessibility support
 * 
 * @component
 * @example
 * // Basic usage
 * <MultiSelectDropdownMC
 *   id="users-select"
 *   label="Select Users"
 *   options={[
 *     { id: 1, label: "John Doe" },
 *     { id: 2, label: "Jane Smith" }
 *   ]}
 *   onSelect={(name, values) => console.log(values)}
 * />
 * 
 * @example
 * // With search and create options
 * <MultiSelectDropdownMC
 *   id="tags-select"
 *   label="Tags"
 *   options={tagOptions}
 *   search={true}
 *   allowCreate={true}
 *   onCreateOption={async (term) => await createTag(term)}
 *   maxSelected={5}
 *   placeholder="Select or create tags..."
 * />
 * 
 * @example
 * // With React Hook Form
 * <MultiSelectDropdownMC
 *   id="categories-select"
 *   name="categoryIds"
 *   label="Categories"
 *   options={categories}
 *   useFloatingLabel={true}
 * />
 * 
 * @param {MultiSelectDropdownProps} props - Component props
 * @returns {React.ReactElement} Rendered multi-select dropdown component
 */

const MultiSelectDropdownMC = memo(({
  id,
  label,
  onSelect,
  defaultValue = [],
  search = false,
  name,
  placeholder,
  filterOption,
  options = [],
  useFloatingLabel = false,
  className = '',
  maxSelected,
  separator,
  allowCreate = false,
  onCreateOption,
  createOptionText = 'Create',
  labelTooltip
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredOptions, setFilteredOptions] = useState(options);
  const [selectedValues, setSelectedValues] = useState(defaultValue || []);
  const [isCreating, setIsCreating] = useState(false);
  const controlForm = useFormContext();
  const inputRef = useRef(null);
  const previousDefaultValueRef = useRef(null);

  // Sync from defaultValue only when the actual values change (avoid loop from new array references)
  
  // Memoized click outside handler to prevent recreating the ref callback
  const domNode = useClickOutside(
    useCallback(() => {
      setIsOpen(false);
      setSearchTerm(''); // Limpiar filtro al cerrar
    }, [])
  );

  // Memoized default value initialization effect
  useEffect(() => {
    const nextValue = defaultValue && Array.isArray(defaultValue)
      ? defaultValue
      : [];
    const prevValue = previousDefaultValueRef.current;
    const hasChanged = !prevValue
      || prevValue.length !== nextValue.length
      || nextValue.some((val, i) => prevValue[i] !== val);
    if (hasChanged) {
      previousDefaultValueRef.current = nextValue;
      setSelectedValues(nextValue);
    }
  }, [defaultValue]);

  // Optimized filtering effect with memoization
  useEffect(() => {
    const filtered = filterOption
      ? filterOption(searchTerm)
      : options.filter((option) => option.label?.toLowerCase().includes(searchTerm.toLowerCase())
          && !selectedValues.includes(option.id));
    setFilteredOptions(filtered);
  }, [searchTerm, options, selectedValues, filterOption]);

  // Memoized handlers for stable references
  const handleSelect = useCallback((optionId, field) => {
    let newSelectedValues;

    if (selectedValues.includes(optionId)) {
      // Deselect if already selected
      newSelectedValues = selectedValues.filter((idValue) => idValue !== optionId);
    } else {
      // Select new option
      if (maxSelected && selectedValues.length >= maxSelected) return;
      newSelectedValues = [...selectedValues, optionId];
    }

    setSelectedValues(newSelectedValues);
    field?.field?.onChange(newSelectedValues);
    if (onSelect) onSelect(name, newSelectedValues);
    if (controlForm?.formState?.errors[name]) controlForm?.trigger(name);
  }, [selectedValues, maxSelected, onSelect, name, controlForm]);

  const handleCreateOption = useCallback(async (field) => {
    if (!searchTerm.trim() || !onCreateOption || isCreating) return;
    
    setIsCreating(true);
    try {
      const newOption = await onCreateOption(searchTerm.trim());
      if (newOption && newOption.id) {
        // Add the new option to selected values
        const newSelectedValues = [...selectedValues, newOption.id];
        setSelectedValues(newSelectedValues);
        field?.field?.onChange(newSelectedValues);
        if (onSelect) onSelect(name, newSelectedValues);
        
        // Clear search term
        setSearchTerm('');
      }
    } catch (error) {
      console.error('Error creating new option:', error);
    } finally {
      setIsCreating(false);
    }
  }, [searchTerm, onCreateOption, isCreating, selectedValues, onSelect, name]);

  // Memoized remove handler
  const removeSelected = useCallback((valueToRemove) => {
    const newSelectedValues = selectedValues.filter((value) => value !== valueToRemove);
    setSelectedValues(newSelectedValues);
    if (onSelect) onSelect(name, newSelectedValues);
    if (controlForm?.control) {
      controlForm.setValue(name, newSelectedValues);
    }
  }, [selectedValues, onSelect, name, controlForm]);

  // Memoized toggle handler
  const toggleDropdown = useCallback(() => {
    setIsOpen(!isOpen);
    if (isOpen) {
      setSearchTerm(''); // Limpiar filtro al cerrar
    }
    if (isOpen && inputRef.current) inputRef.current.focus();
  }, [isOpen]);

  // Memoized create option availability check
  const canShowCreateOption = useCallback(() => {
    return allowCreate && 
           searchTerm.trim() && 
           filteredOptions.length === 0 && 
           onCreateOption &&
           !options.some(option => option.label?.toLowerCase() === searchTerm.toLowerCase());
  }, [allowCreate, searchTerm, filteredOptions.length, onCreateOption, options]);

  // Memoized CSS classes to prevent recreation
  const buttonClassName = useMemo(() => [
    'tw-bg-gray-50',
    'tw-text-default',
    'tw-rounded-xl',
    'tw-w-full',
    'tw-px-4',
    'tw-flex',
    'tw-items-center',
    'tw-justify-between',
    'tw-border',
    'tw-relative',
    'tw-min-h-[42px]',
    useFloatingLabel ? 'tw-pt-5 tw-pb-1' : 'tw-py-2.5'
  ].join(' '), [useFloatingLabel]);

  const dropdownClass = useMemo(() => [
    'tw-absolute',
    'tw-bg-white',
    'tw-border',
    'tw-rounded-md',
    'tw-shadow-lg',
    'tw-z-50',
    'tw-w-full',
    'tw-mt-1'
  ].join(' '), []);

  const listItemClassName = useMemo(() => [
    'tw-px-4',
    'tw-py-2',
    'hover:tw-bg-blue-100',
    'tw-flex',
    'tw-items-center',
    'tw-cursor-pointer',
    'tw-break-words'
  ].join(' '), []);

  const floatingLabelClassNames = useMemo(() => [
    'tw-absolute',
    'tw-top-[5px]',
    'tw-z-10',
    'tw-start-4',
    'tw-end-12',
    'tw-flex',
    'tw-items-center',
    'tw-gap-1'
  ].join(' '), []);

  // Memoized selected tags rendering to prevent unnecessary re-renders
  const selectedTags = useMemo(() => {
    return selectedValues.map((value, idx) => {
      const option = options.find((opt) => String(opt.id) === String(value));
      return option ? (
        <React.Fragment key={option.id}>
          {(separator && idx > 0) && separator()}
          <TagMC
            label={option.label}
            onClick={(e) => {
              e.stopPropagation();
              removeSelected(value);
            }}
          />
        </React.Fragment>
      ) : null;
    });
  }, [selectedValues, options, separator, removeSelected]);

  // Memoized dropdown content to prevent unnecessary re-renders
  const dropdownContent = useCallback((field) => (
    <div className={`tw-wrapper tw-relative tw-w-full ${className}`} ref={domNode}>
      {!useFloatingLabel && label && (
        <label htmlFor={id} className="tw-text-zinc-700 tw-mb-1 tw-text-sm tw-font-bold">
          {label}
        </label>
      )}

      <button
        type="button"
        onClick={toggleDropdown}
        className={buttonClassName}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-label={label}
      >
        <div className="tw-w-full">
          {(useFloatingLabel) && (
            <div className={floatingLabelClassNames}>
              <label htmlFor={id} className="tw-mb-0 tw-text-xs tw-text-blue-500 tw-truncate">
                {label}
              </label>
              {labelTooltip && (
                <TooltipMC text={labelTooltip} position="right">
                  <IconMC name="info-outline" size="xs" className="tw-text-blue-500 tw-flex-shrink-0" />
                </TooltipMC>
              )}
            </div>
          )}

          <div className="tw-flex tw-flex-wrap tw-gap-2 tw-items-center tw-overflow-hidden tw-text-gray-700">
            {selectedValues.length === 0 ? (
              <span className="tw-text-gray-400 tw-text-default/[26px]">
                {placeholder}
              </span>
            ) : (
              selectedTags
            )}
          </div>

          <div className="tw-absolute tw-top-1/2 tw-right-4 tw-transform -tw-translate-y-1/2">
            <IconMC
              name={`arrow-ios-${isOpen ? 'up' : 'down'}ward-outline`}
              size="base"
              className="tw-text-gray-600"
            />
          </div>
        </div>
      </button>

      {controlForm?.formState?.errors[name] && (
        <p className="tw-text-xs tw-text-red-500">
          {controlForm?.formState?.errors[name].message}
        </p>
      )}

      {isOpen && (
        <div className={dropdownClass} role="listbox" aria-label={label}>
          {search && (
            <div className="tw-px-2 tw-pt-2">
              <SearchComponent
                placeholder={placeholder || t('dropdown_mc.search')}
                onChange={setSearchTerm}
                inputRef={inputRef}
                inputClassName="tw-text-sm tw-bg-white"
                debounceTime={200}
              />
            </div>
          )}

          <ul className="tw-py-1 tw-overflow-y-auto tw-max-h-60 tw-overflow-x-hidden tw-pl-0" role="group">
            {filteredOptions.length === 0 && !canShowCreateOption() ? (
              <li key="no-option" className="tw-px-4 tw-py-2 tw-text-gray-500" role="option">
                {t('dropdown_mc.no_options')}
              </li>
            ) : (
              <>
                {filteredOptions.map((option) => (
                  <li
                    key={option.id}
                    onClick={() => handleSelect(option.id, field)}
                    className={`${listItemClassName} ${
                      selectedValues.includes(option.id) ? 'tw-bg-blue-500 tw-text-white' : ''
                    }`}
                    role="option"
                    aria-selected={selectedValues.includes(option.id)}
                  >
                    {option.icon && <span className={`tw-text-xs tw-mr-2 ${option.icon}`} />}
                    <span className="tw-overflow-hidden tw-block tw-text-ellipsis tw-w-4/5">
                      {option.label}
                    </span>
                  </li>
                ))}
                {canShowCreateOption() && (
                  <li
                    key="create-option"
                    onClick={() => handleCreateOption(field)}
                    className={`${listItemClassName} tw-border-t tw-border-gray-200 tw-bg-green-50 hover:tw-bg-green-100 tw-text-green-700 ${
                      isCreating ? 'tw-opacity-50 tw-cursor-not-allowed' : ''
                    }`}
                    role="option"
                    aria-disabled={isCreating}
                  >
                    <IconMC
                      name="add-outline"
                      size="sm"
                      className="tw-mr-2 tw-text-green-600"
                    />
                    <span className="tw-font-medium">
                      {isCreating 
                        ? t('dropdown_mc.creating', { defaultValue: 'Creating...' })
                        : `${createOptionText} "${searchTerm}"`
                      }
                    </span>
                  </li>
                )}
              </>
            )}
          </ul>

          {maxSelected && (
            <div className="tw-px-4 tw-py-2 tw-text-xs tw-text-gray-500 tw-border-t">
              {t('dropdown_mc.selected_count', {
                count: selectedValues.length,
                max: maxSelected
              })}
            </div>
          )}
        </div>
      )}
    </div>
  ), [
    className, domNode, useFloatingLabel, label, id, toggleDropdown, buttonClassName,
    isOpen, floatingLabelClassNames, selectedValues.length, placeholder, selectedTags,
    controlForm, name, dropdownClass, search, t, inputRef, filteredOptions.length,
    canShowCreateOption, filteredOptions, listItemClassName, handleSelect,
    handleCreateOption, isCreating, createOptionText, searchTerm, maxSelected, labelTooltip
  ]);

  // Return the appropriate render based on form context
  if (!controlForm?.control) {
    return dropdownContent();
  }

  return (
    <Controller
      name={name}
      control={controlForm.control}
      defaultValue={defaultValue || []}
      render={dropdownContent}
    />
  );
});

// Display name for debugging and dev tools
MultiSelectDropdownMC.displayName = 'MultiSelectDropdownMC';

export default MultiSelectDropdownMC;
